import { FormatDate } from "../../../_objects/format-date";
import { GUID } from "../../../app.constants";

export class ReportTemplate {
  id: string;
  name: string;
  subjectModelIds: string[];
  schemaVersion: string;
  template: TemplateDefinition;
  isValid: boolean;
  reportTitleInputMode: string;
  reportTitleFormat: ReportTitleFormat[];
  createdBy: string;
  createDate: string;
  lastUpdatedBy: string;
  lastUpdateDate: string;

  public reportTemplateContent?: string;
  public reportType: string;
  public apiCode: string;
  public equipmentTypes?: string | string[];
  public equipmentType?: string | string[];
}

export class ReportTitleFormat {
  value: string;
  type: string;
  format: string;
}

export class ListFormat {
  public name: string;
  public id?: string;
  public listData: string[];
}

export class TemplateDefinition {
  header: TemplateCommonSection;
  bodies: TemplatePageSection[];
  footer: TemplateCommonSection;
  pageMarginTop: number;
  pageMarginBottom: number;
  pageMarginLeft: number;
  pageMarginRight: number;
  paperSize: string;
  sizeUnit: string;
  fontSizeUnit: string;
  fontSize: number;
  fontFamily: string;
  list: ListFormat[];
}

export class TemplateCommonSection {
  id: string;
  height: number;
  texts: TemplateText[];
  images: TemplateImage[];
  lines: TemplateLine[];
  rectangles: TemplateRectangle[];
  inspectionSummary: TemplateSummary;
  inspectionPictures: TemplateSummary;
  dataPlates: TemplateDataPlate;
  signatures: TemplateSignature[];
  tables: TemplateTable[];
}

export class TemplatePageSection {
  id: string;
  height: number;
  hasHeader: boolean;
  hasFooter: boolean;
  texts: TemplateText[];
  images: TemplateImage[];
  lines: TemplateLine[];
  rectangles: TemplateRectangle[];
  inspectionSummary: TemplateSummary;
  inspectionPictures: TemplateSummary;
  dataPlates: TemplateDataPlate;
  measurementPoints?: TemplateMeasurementPoint[];
  visualEvents?: TemplateVisualEvents[];
  signatures?: TemplateSignature[];
  sections?: TemplateSection[];
  tables?: TemplateTableOffline[];
  interactiveIso?: TemplateIso[];
  public dropDownLists?: TemplateDropDownList[];
  public customElements?: TemplateCustomElements[];
}

export class TemplateCustomPageSection {
  public sections?: TemplateSection[];
  public texts?: TemplateText[];
  public images?: TemplateImage[];
  public lines?: TemplateLine[];
  public rectangles?: TemplateRectangle[];
  public dropDownLists?: TemplateDropDownList[];
  public tables?: TemplateTable[];
}

export class SectionedTemplatePage extends TemplatePageSection {
  public sectionId: string;
  public sectionLeft: number;
  public sectionTop: number;
  public sectionWidth: number;
  public sectionHeight: number;
  public zIndex: number;
  public stageId: string;
}

export class ElementLocationInSection {
  public x: string;
  public y: string;
}

export class SectionInfo {
  public sectionId?: string;
  public sectionStageTag?: string;
  public elementLocationInSection?: ElementLocationInSection;
}

export class TemplateTextItem {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  padding: string;
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  zIndex: number;
  text: string;
  fontSize: number;
  fontFamily: string;
  fontColor: string;
  fontStyle: string;
  fontWeight: string;
  lineHeight: number;
  horizontalAlign: string;
  isHorizontal: boolean;
  verticalAlign: string;
  borderStyle: string;
  borderWidth: number;
  borderColor: string;
  backgroundColor: string;
}

export class TemplateText extends TemplateTextItem {
  type: string;
  format: string;
  dynamicDataField: string;
  dynamicId: string;
  inputType: string;
  editableId: string;
  validation: Validation;
  radioType: string;
  radioLabel: string;
  options: Options[];
  label: string;
  value: string;
  inputGroup: string;
  multiSelectDelimiter: string;
  isTableText: boolean = false;
}

export class TemplateImage extends SectionInfo {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  src: string;
  type: string;
  dynamicDataField: string;
  dynamicId: string;
}

export class TemplateIso extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public isoType: "Redline" | "VisualEvent";
  public dynamicDataField: string;
}

export class TemplateLine {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  borderStyle: string;
  borderColor: string;
  length: number;
  isHorizontal: boolean;
}

export class TemplateRectangle {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  borderStyle: string;
  borderWidth: string;
  borderColor: string;
  backgroundColor: string;
}

export class TemplateSection {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public stageId: string;
}

export class TemplateTable {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  headerHeight: number;
  headerBackgroundColor: string;
  headerFontSize: number;
  headerFontFamily: string;
  horizontalAlign: string;
  borderStyle: string;
  hasBorderTop: boolean;
  hasBorderBottom: boolean;
  hasBorderLeft: boolean;
  hasBorderRight: boolean;
  hasRowBackground: boolean;
  rowBackgroundColor: string;
  columns: object;
}

export class TemplateTableOffline extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public tableType: string;
  public headerHeight: number;
  public headerBackgroundColor: string;
  public fontSize: number;
  public cellBorder: string;
  public cellPadding: number;
  public isStriped: boolean;
  public type: string;
  public dynamicDataField?: string;
  public columnList?: ColumnList[];
  public rowList?: RowList[];
  public priorityList?: PriorityList[];
}

export class ColumnList {
  public label: string;
  public width: number;
  public inputType?: string;
  public textOrientation?: string;
  public tagging?: string;
  public textAlignment?: string;
  public needsPriority?: boolean;
  public dataField?: string;
}

export class RowList {
  public label: string;
  public tagging?: string;
  public textAlignment?: string;
  public isCategory?: boolean;
}

export class PriorityList {
  public code: string;
  public label: string;
}

export class TemplateSummaryTable extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public headerHeight: number;
  public headerBackgroundColor: string;
  public headerFontSize: number;
  public headerFontFamily: string;
  public horizontalAlign: string;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public hasBorderTop: boolean;
  public hasBorderBottom: boolean;
  public hasBorderLeft: boolean;
  public hasBorderRight: boolean;
  public hasRowBackground: boolean;
  public rowBackgroundColor: string;
  public columns: TableColumn[];
  public tableMode: boolean;
  public dataMode: number;
}

export abstract class TableColumn {
  public header: string;
  public headerType: string;
  public texts: string[];
  public fontSize: number;
  public fontFamily: string;
  public horizontalAlign: string;
  public isHorizontal: boolean;
  public verticalAlign: string;
  public padding: string;
  public widthRatio: number;
  public isHeaderVertical: boolean;
  public isColumnVertical: boolean;
  public isCombined: boolean;
  public isItemsHorizontal: boolean;
  public textsInfo: TemplateText[];
}

export class TemplateMeasurementPoint extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public fontSize: number;
  public fontFamily: string;
  public headerHeight: number;
  public headerBackgroundColor: string;
  public headerFontSize: number;
  public headerFontFamily: string;
  public horizontalAlign: string;
  public hasBorderTop: boolean;
  public hasBorderBottom: boolean;
  public hasBorderLeft: boolean;
  public hasBorderRight: boolean;
  public hasRowBackground: boolean;
  public rowBackgroundColor: string;
  public columns: ListItem[];
  public tableBorderWidth: number;
  public tableBorderStyle: string;
  public tableBorderColor: string;
  public tableMargin: number;
  public tableRules: string;
  public cellMargin: number;
  public isStriped: boolean;
  public dynamicDataField: string;
  public tableWidth?: number;
  public titleText: string;
  public titleFontSize: number;
  public titleAlign: string;
  public titleBackgroundColor: string;
}

export class ListItem {
  public label: string;
  public dataField?: string;
  public type?: "Dynamic" | "Editable";
  public inputType?: string;
  public optionList?: Array<string>;
}

export class TemplateVisualEvents extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public fontSize: number;
  public fontFamily: string;
  public dynamicDataField: string;
  public itemDataSource: string;
  public itemWidth: number;
  public itemHeight: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public itemMargin: number;
  public maxItemCount: number;
  public captionsList: ListItem[];
  public itemContainerHeight?: number;
  public titleText: string;
  public titleFontSize: number;
  public titleAlign: string;
  public titleBackgroundColor: string;
}

export class TemplateCustomElements extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public fontSize: number;
  public fontFamily: string;
  public dynamicDataField: string;
  public itemDataSource: string;
  public itemWidth: number;
  public itemHeight: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public itemMargin: number;
  public maxItemCount: number;
  public captionsList: ListItem[];
  public itemContainerHeight?: number;
  public titleText: string;
  public titleFontSize: number;
  public titleAlign: string;
  public titleBackgroundColor: string;
  public designElementId: string;
  public designElementName: string;
}

export class TemplateSignature extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public reservedFor: string;
}

export class TemplateSummary extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public defaultPageBreakRowCount: number;
  public hasInspectionSummaryLink: boolean;
  public title: TemplateText;
  public table: TemplateSummaryTable;
  public subTotal: SubTotal;
  public isEmptyFolderDisplayed: boolean;
}

export class SubTotal extends SectionInfo {
  public title: TemplateText;
  public table: TemplateSummaryTable;
}

export class TemplateDataPlate extends SectionInfo {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  title: TemplateText;
  imageInfo: TemplateDataPlateImageInfo;
}
export class TemplateDataPlateText extends TemplateText {
  position: string;
}
export class TemplateDataPlateImageInfo extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  width: number;
  height: number;
  title: TemplateDataPlateText;
  image: TemplateImage;
}

export class ReportTemplateNew {
  name: string;
  subjectModelIds: string[];
  template: TemplateDefinition;
  isValid: boolean;
  reportTitleInputMode: string;
  reportTitleFormat: ReportTitleFormat[];
  schemaVersion: string;
}

export class ReportTemplateUpdate {
  id: string;
  name: string;
  subjectModelIds: string[];
  template: TemplateDefinition;
  isValid: boolean;
  reportTitleInputMode: string;
  reportTitleFormat: ReportTitleFormat[];
  schemaVersion: string;
}

export class TemplateDropDownList extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public selectedValue: string;
  public options: Options[];
}
export class ReportTemplateListItem {
  id: string;
  name: string;
  isValid: boolean;
  lastUpdateDate: string;
  reportTitleInputMode: string;
  reportTitleFormat: ReportTitleFormat[];
  /** オフラインで作成したかどうか。 */
  isOfflineData: boolean;
  public application?: string;
  public templateUrl?: string;
  public pdfPreviewPath?: string;
  public createdByTenantId?: string;
  public createdByUserId?: string;
  public createdDateUtc?: string;
  public lastUpdatedDateUtc?: string;
  public reportTemplateContent?: string;
  public subjectModelIds?: string[];
  public action?: string;
  public reportType?: string;
  public equipmentTypes?: string | string[];
  public equipmentType?: string | string[];
  public apiCode?: string;
}

export class EditableTextList {
  editableId: string;
  inputType: string;
  label: string;
  radioLabel: string;
  value: string;
  text: string;
  validation: Validation;
  options: Options[];
  inputGroup: string;
}

export class Validation {
  required: boolean;
  maxLength: number;
}

export class Options {
  label: string;
  value: string;
}

export class UpdateIdList {
  subjectModelIds: string[];
}

export class UpdateResultList {
  updateResult: ResponseIdList[];
}

export class ResponseIdList {
  reportTemplateId: string;
  subjectModelId: string;
}

export class ReportTemplateImageList {
  fileName: string;
  originalUrl: string;
  thumbUrl: string;
}

export class IndividualReportTemplate extends ReportTemplate {
  imagePathList: ImagePath[];
  tenantId: string;
  isDeleted: boolean;
  /** オフラインで作成したかどうか。 */
  isOfflineData: boolean;

  public setValueFromJsonObject(jsonValue: any, userId: string, tenantId: string): string {
    // TODO: IPLEX Mode チェック処理

    const now: FormatDate = new FormatDate();

    this.id = GUID.generateUUID();
    this.name = jsonValue.name;
    this.subjectModelIds = new Array<string>();
    this.schemaVersion = jsonValue.schemaVersion;
    this.template = jsonValue.template;
    this.isValid = true;
    this.reportTitleInputMode = jsonValue.reportTitleInputMode;
    this.reportTitleFormat = jsonValue.reportTitleFormat;
    this.createdBy = userId;
    this.createDate = now.toString();
    this.lastUpdatedBy = userId;
    this.lastUpdateDate = now.toString();
    this.tenantId = tenantId;
    this.imagePathList = new Array<ImagePath>();
    this.isDeleted = false;
    this.isOfflineData = true;

    return "";
  }
}

export class ImagePath {
  id: string;
  src: string;
}

export class ReportGlobalImage {
  public id: string;
  public name: string;
  public createdBy: string;
  public dateCreatedUtc: string;
  public dateUpdatedUtc: string;
  public imageUrl: string;
}

export class AddCustomElement {
  public name: string;
  public tenantid: string;
  public application?: string;
  public templateUrl: string;
}

export class ReportCustomElements {
  public id: string;
  public tenantId: string;
  public name: string;
  public application: string;
  public templateUrl: string;
}
